// SceneRegistry.js - 世界场景注册表（sceneKey ↔ mapKey ↔ 数据文件）
import DebugHelper from './DebugHelper.js';

/**
 * @typedef {Object} SceneRegistryEntry
 * @property {string} mapKey - Phaser cache.json 的 key
 * @property {string} jsonPath - 地图 JSON 资源路径
 * @property {string} displayName - 显示名称
 */

/** @type {Record<string, SceneRegistryEntry>} */
export const SCENE_REGISTRY = {
    WorldScene: {
        mapKey: 'world_meta',
        jsonPath: 'assets/data/world_meta.json',
        displayName: '主世界'
    },
    map_rado: {
        mapKey: 'map_rado',
        jsonPath: 'assets/data/rado_town.json',
        displayName: '拉多镇'
    },
    map_mack: {
        mapKey: 'map_mack',
        jsonPath: 'assets/data/mack_town.json',
        displayName: '麦基镇'
    },
    map_bar_1: {
        mapKey: 'map_bar_1',
        jsonPath: 'assets/data/bar_1.json',
        displayName: '酒吧'
    },
    map_human_1: {
        mapKey: 'map_human_1',
        jsonPath: 'assets/data/human_1.json',
        displayName: '人类商店'
    },
    map_hotel_1: {
        mapKey: 'map_hotel_1',
        jsonPath: 'assets/data/hotel_1.json',
        displayName: '旅馆1'
    },
    map_hotel_2: {
        mapKey: 'map_hotel_2',
        jsonPath: 'assets/data/hotel_2.json',
        displayName: '旅馆2'
    },
    map_tank_center_1: {
        mapKey: 'map_tank_center_1',
        jsonPath: 'assets/data/tank_center_1.json',
        displayName: '战车中心'
    },
    map_cave_1: {
        mapKey: '洞穴1',
        jsonPath: 'assets/data/cave_1.json',
        displayName: '拉多南山洞'
    },
    map_cave_2: {
        mapKey: '洞穴2',
        jsonPath: 'assets/data/cave_2.json',
        displayName: '洞穴2'
    },
    map_cave_3: {
        mapKey: '拉多北山洞',
        jsonPath: 'assets/data/cave_north.json',
        displayName: '拉多北山洞'
    }
};

/** Phaser 动态地图场景：sceneKey -> mapKey */
export const MAP_SCENE_CONFIG = Object.fromEntries(
    Object.entries(SCENE_REGISTRY).map(([sceneKey, entry]) => [sceneKey, entry.mapKey])
);

/** 已注册的可传送世界场景 key */
export const VALID_WORLD_SCENE_KEYS = Object.freeze(Object.keys(SCENE_REGISTRY));

const WORLD_SCENE_KEY_SET = new Set(VALID_WORLD_SCENE_KEYS);

/**
 * 是否为已注册的世界场景
 * @param {string} sceneKey
 * @returns {boolean}
 */
export function isRegisteredWorldScene(sceneKey) {
    return typeof sceneKey === 'string' && WORLD_SCENE_KEY_SET.has(sceneKey);
}

/**
 * 获取场景显示名
 * @param {string} sceneKey
 * @returns {string}
 */
export function getSceneDisplayName(sceneKey) {
    return SCENE_REGISTRY[sceneKey]?.displayName || sceneKey || '未知';
}

/**
 * 根据 mapKey 反查 sceneKey
 * @param {string} mapKey
 * @returns {string|null}
 */
export function getSceneKeyByMapKey(mapKey) {
    const entry = Object.entries(SCENE_REGISTRY).find(([, config]) => config.mapKey === mapKey);
    return entry ? entry[0] : null;
}

/**
 * 校验传送目标是否合法
 * @param {string} targetScene
 * @returns {{ valid: boolean, sceneKey?: string, reason?: string }}
 */
export function resolveTransitionTarget(targetScene) {
    if (!targetScene || typeof targetScene !== 'string') {
        return { valid: false, reason: 'empty_target' };
    }

    if (isRegisteredWorldScene(targetScene)) {
        return { valid: true, sceneKey: targetScene };
    }

    return { valid: false, reason: 'unknown_scene', targetScene };
}

/**
 * 从地图 JSON 收集所有传送目标 sceneKey
 * @param {Object} mapJson
 * @returns {Array<{ targetScene: string, source: string }>}
 */
export function collectMapTransitionTargets(mapJson) {
    if (!mapJson || typeof mapJson !== 'object') {
        return [];
    }

    const targets = [];

    const pushTarget = (targetScene, source) => {
        if (targetScene) {
            targets.push({ targetScene, source });
        }
    };

    (mapJson.towns || []).forEach((town, index) => {
        pushTarget(town.map_name, `towns[${index}].map_name (${town.name || '未命名'})`);
    });

    (mapJson.caves || []).forEach((cave, index) => {
        pushTarget(cave.map_name, `caves[${index}].map_name (${cave.name || '未命名'})`);
    });

    (mapJson.buildings || []).forEach((building, index) => {
        pushTarget(building.map_name, `buildings[${index}].map_name (${building.name || '未命名'})`);
    });

    (mapJson.tankcenter || []).forEach((center, index) => {
        pushTarget(center.map_name, `tankcenter[${index}].map_name (${center.name || '未命名'})`);
    });

    (mapJson.exits || []).forEach((exit, index) => {
        pushTarget(exit.targetScene, `exits[${index}].targetScene`);
    });

    return targets;
}

/**
 * 校验单张地图 JSON 中的传送目标
 * @param {Object} mapJson
 * @param {string} sourceLabel
 * @returns {Array<{ targetScene: string, source: string, sourceMap: string }>}
 */
export function findInvalidMapTargets(mapJson, sourceLabel) {
    const invalid = [];

    collectMapTransitionTargets(mapJson).forEach(({ targetScene, source }) => {
        if (!isRegisteredWorldScene(targetScene)) {
            invalid.push({
                targetScene,
                source,
                sourceMap: sourceLabel
            });
        }
    });

    return invalid;
}

/**
 * 启动时校验所有已加载地图的传送目标
 * @param {Phaser.Scene} scene
 * @returns {Array<{ targetScene: string, source: string, sourceMap: string }>}
 */
export function validateAllLoadedMaps(scene) {
    const allInvalid = [];

    Object.entries(SCENE_REGISTRY).forEach(([sceneKey, config]) => {
        const mapJson = scene.cache?.json?.get(config.mapKey);

        if (!mapJson) {
            DebugHelper.warn(
                `[SceneRegistry] 地图数据未加载: scene=${sceneKey}, mapKey=${config.mapKey}, path=${config.jsonPath}`
            );
            return;
        }

        const invalidTargets = findInvalidMapTargets(mapJson, sceneKey);
        invalidTargets.forEach((issue) => {
            DebugHelper.warn(
                `[SceneRegistry] 无效传送目标: ${issue.targetScene} ` +
                `(来自 ${issue.sourceMap} -> ${issue.source})`
            );
            allInvalid.push(issue);
        });
    });

    if (allInvalid.length > 0) {
        DebugHelper.warn(`SceneRegistry: 共发现 ${allInvalid.length} 个无效传送目标`);
    } else {
        DebugHelper.log('SceneRegistry: 所有已加载地图的传送目标校验通过');
    }

    return allInvalid;
}

/**
 * @deprecated 使用 validateAllLoadedMaps
 */
export function assertMapTargetsRegistered(mapJson, sourceLabel) {
    return findInvalidMapTargets(mapJson, sourceLabel);
}

/**
 * 解析当前应使用的世界场景 key（优先 preferred，其次 active/paused）
 * @param {Phaser.Scenes.SceneManager} sceneManager
 * @param {string|null} preferredSceneKey
 * @returns {string|null}
 */
export function resolveWorldSceneKey(sceneManager, preferredSceneKey = null) {
    if (preferredSceneKey && isRegisteredWorldScene(preferredSceneKey)) {
        const preferred = sceneManager.get(preferredSceneKey);
        if (preferred && (preferred.scene.isActive() || preferred.scene.isPaused())) {
            return preferredSceneKey;
        }
    }

    for (const key of VALID_WORLD_SCENE_KEYS) {
        const scene = sceneManager.get(key);
        if (scene && (scene.scene.isActive() || scene.scene.isPaused())) {
            return key;
        }
    }

    return null;
}

/**
 * 获取当前 active/paused 的世界场景实例
 * @param {Phaser.Scenes.SceneManager} sceneManager
 * @param {string|null} preferredSceneKey
 * @returns {Phaser.Scene|null}
 */
export function getWorldScene(sceneManager, preferredSceneKey = null) {
    const sceneKey = resolveWorldSceneKey(sceneManager, preferredSceneKey);
    return sceneKey ? sceneManager.get(sceneKey) : null;
}

export const DEFAULT_WORLD_SCENE_KEY = 'WorldScene';

/**
 * 解析玩家初始/存档场景 key
 * @param {string|null|undefined} initialScene
 * @returns {string}
 */
export function resolveInitialSceneKey(initialScene) {
    if (initialScene && isRegisteredWorldScene(initialScene)) {
        return initialScene;
    }
    return DEFAULT_WORLD_SCENE_KEY;
}

/**
 * 从存档/游戏状态解析应恢复的场景 key
 * @param {Object|null} gameState
 * @returns {string}
 */
export function getResumeSceneKey(gameState) {
    if (!gameState) {
        return DEFAULT_WORLD_SCENE_KEY;
    }
    const scene = gameState.currentScene
        || gameState.player?.currentScene
        || gameState.player?.initialScene;
    return resolveInitialSceneKey(scene);
}
